// Global "workboard": the items the user has collected — kept across navigation so they can be used
// later (saved to a story, turned into a task, etc.). Items are full objects:
//   { id, kind: 'photo'|'link'|'note'|'receipt', title, url, content, query }
// Anything with a stable id can be added: research results, saved-story items, timeline items.
//
// The board is SCOPED to the context it was filled in (busy-my-web#84): it survives every move
// inside that story / research session, and is dropped on the way out of it.
import { boardShowsOpenPacks, enterContextEffect } from '@/lib/workboardContext'
// An item that stands for a real server-side object also carries `objectRef: { type, id }`
// (busy-timeline-srv#20) — stamped on the way in, so every surface that puts something on the board
// gets it without being rewritten. Additive only: no id changes, nothing reads it yet.
import { withObjectRef } from '@/lib/objectRef'

// WHERE the board lives (busy-my-web#84). It was localStorage under `busy.workboard.v1`, which
// meant weeks-old selections greeted Fatemeh in a browser she had not used since — before she even
// signed in. The board is SESSION state, so it is stored per browsing session:
//
//   • a browser opened days later starts with an empty board (a new session has no storage);
//   • signing out already wipes it — `auth.service.signOut()` calls `sessionStorage.clear()`;
//   • the guest → login round-trip still works, which is why the board is persisted at all: the
//     IDS redirect is a full-page navigation in the SAME tab, and sessionStorage survives that
//     (and the reload back), so a guest's collected findings are still there when they return.
//
// v2 also carries the OWNER context (see lib/workboardContext.js) next to the items, so a restore
// knows which ongoing / research session the board belongs to. v1 payloads have no owner and are
// exactly the stale debris this fixes, so they are dropped — and their key removed — on first load.
const STORAGE_KEY = "busy.workboard.v2";
const LEGACY_KEYS = ["busy.workboard.v1"];
const EMPTY = { owner: null, items: [] };

function load() {
  // Clear out the pre-#84 board wherever it was left behind, once, on first load.
  try { for (const k of LEGACY_KEYS) { localStorage.removeItem(k); sessionStorage.removeItem(k) } } catch { /* storage blocked */ }
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    if (!saved || !Array.isArray(saved.items)) return { ...EMPTY };
    return { owner: saved.owner || null, items: saved.items };
  } catch { return { ...EMPTY }; }
}
function persist(s) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ owner: s.owner || null, items: s.items })); } catch { /* storage full/blocked */ }
}

const restored = load();

export const state = {
  items: restored.items,
  // The context the board was filled in — `ongoing:<id>`, `home-research`, … Set from the current
  // route the first time anything lands on an empty board, and what leaving clears against.
  owner: restored.owner,
  // The context the app is in RIGHT NOW, kept by the router wiring (router/index.js).
  context: null,
  // A context the board has been HANDED OVER to, and has not arrived at yet (busy-ai-srv#13): the
  // one navigation that must not empty the board, because the feature performing it is what put the
  // rows there. Consumed by the next ENTER_CONTEXT, whichever context that turns out to be — never
  // persisted, since it only spans the navigation it was declared for.
  handOff: null,
  // The item currently opened in the middle-of-page preview (image / doc / link), or null.
  preview: null,
  // Where the preview was opened from: 'gallery' (an Images tab / search — show the picture list
  // switcher in the panel) or 'timeline' (a timeline card — no list, just the picture).
  previewSource: null,
  // Document ids the user has taken OFF the board from the open entity's attachment group
  // (busy-my-web#400). These files were never ticked — they are on the board because the thing
  // holding them is open — so taking one off is a statement about the BOARD and nothing else: the
  // file stays attached to its task/note/event, and this list is the only record of the removal.
  // Not persisted, and dropped on the way out of the context, for the same reason: it is a view.
  detachedDocs: [],
}

// An opened Research Pack rides on the board as CONTEXT (Pouria, 2026-08-17: "put opened resarach
// pack to work board and keep it until user remove it from work board"). It is kept in the same
// persisted list — one board, one storage key — but it is NOT part of the selection: `items` is what
// a save writes and what the Save verbs count, so a pack must never leak into it. Hence one store,
// two views of it.
const isPack = i => i && i.kind === 'pack'

// A SENDER pick — a group (#99) or an ongoing's email connection (#105) — is on the same board and
// IS part of the selection (the box's "Selected" list names them and counts their emails), but it
// is not something a Research Pack save can be written from. `count` is what the save card and the
// research suggestions read to decide "there is a selection to save", so a sender counted there
// opened the "Research pack name" card over a ticked connection, asking where to file a person.
//
// A ticked THREAD used to be excluded with them, and is not any more (busy-timeline-srv#35): an
// email IS a finding, saving one into a pack is the spec's strong evidence that the thread belongs
// to the story (§9 row 5), and it has the one thing a sender lacks — a single thing to open. The
// distinction is the whole point of splitting this rule in two: a sender still cannot be filed.
const isSenderPick = i => i && (i.kind === 'email-connection' || i.kind === 'email-group')

// A reply draft's SOURCES (busy-ai-srv#13) ride on the board the way an opened pack does: they are
// context — what the draft in the composer was written from — and not a selection. Nobody ticked
// them, so they must not be counted by the save verbs, must not be written by a save, and a Clear
// must not pretend to unpick them. Their own list, replaced whole each time a draft is made.
const isEvidence = i => i && i.kind === 'evidence'

export const getters = {
  // What has been COLLECTED — the selection. Packs and a draft's sources are context and stay out.
  items: s => s.items.filter(i => !isPack(i) && !isEvidence(i)),
  // …and the packs the user has opened, in the order they were opened.
  openPacks: s => s.items.filter(isPack),
  // …and the ones the board DRAWS: none while a task/note/event/contact/call is being created
  // (busy-my-web#602 — the creation's board starts empty; the open pack is its context, not a
  // chip). The packs stay in `openPacks` and come back the moment the creation ends.
  boardPacks: (s, g, rootState, rootGetters) =>
    (boardShowsOpenPacks(rootGetters && rootGetters['focusedSession/session']) ? s.items.filter(isPack) : []),
  // …and the sources behind the reply draft in the composer, in the order the drafter ranked them.
  evidence: s => s.items.filter(isEvidence),
  ids: s => s.items.filter(i => !isPack(i) && !isEvidence(i)).map(i => i.id),
  count: s => s.items.filter(i => !isPack(i) && !isEvidence(i)).length,
  // …and how many of them a SAVE could be written from — see `isEmailPick`. The board opens for any
  // selection (that is `count`); only this one answers "is there research in hand to file?".
  saveableCount: s => s.items.filter(i => !isPack(i) && !isSenderPick(i) && !isEvidence(i)).length,
  has: s => id => s.items.some(i => i.id === id),
  preview: s => s.preview,
  previewSource: s => s.previewSource,
  /** Documents taken off the board from the open entity's attachments (busy-my-web#400). */
  detachedDocs: s => s.detachedDocs || [],
  // The context that owns what is on the board (null while the board is empty), and where we are.
  owner: s => s.owner || null,
  context: s => s.context || null,
  // "3 photos · 1 note · 1 link" summary.
  summary: s => {
    const counts = {}
    for (const it of s.items.filter(i => !isPack(i) && !isEvidence(i))) counts[it.kind] = (counts[it.kind] || 0) + 1
    return Object.entries(counts).map(([k, n]) => `${n} ${k}${n === 1 ? '' : 's'}`).join(' · ')
  },
}

// The board takes its owner from the context it was filled in, and gives it back when it empties —
// so the next thing collected, wherever that is, starts a fresh ownership.
function stamp(s) {
  if (s.items.length === 0) s.owner = null
  else if (!s.owner) s.owner = s.context || null
}

export const mutations = {
  SET(s, items) { s.items = (items || []).map(withObjectRef); stamp(s); persist(s) },
  ADD(s, item) { if (!s.items.some(i => i.id === item.id)) { s.items = [...s.items, withObjectRef(item)]; stamp(s); persist(s) } },
  REMOVE(s, id) { s.items = s.items.filter(i => i.id !== id); stamp(s); persist(s) },
  // Arriving somewhere: if the board belongs to a context we have just left, it goes with it
  // (Pouria, 2026-08-18: "when we go out should be removed") — packs included, because an opened
  // pack is context and its story is precisely what was left. An open preview goes too: it is a
  // window onto an item that is no longer collected.
  // …unless the board was HANDED OVER to the context we are arriving at (busy-ai-srv#13): a reply
  // draft writes its sources and then takes the user to the composer itself, so those rows travel
  // with the navigation and the composer becomes their owner. Leaving the composer clears them.
  ENTER_CONTEXT(s, ctx) {
    const effect = enterContextEffect({ owner: s.owner, handOff: s.handOff, next: ctx || null })
    if (effect.clear) {
      s.items = []
      s.preview = null
      s.previewSource = null
    }
    // The detached-file list belongs to the entity that was open, so it never survives a
    // navigation — arriving somewhere new, every attachment is back on the board.
    s.detachedDocs = []
    s.owner = effect.owner
    s.handOff = null
    s.context = ctx || null
    // A board restored from a previous page load (the guest → login round-trip) is adopted by the
    // context it comes back into, so it is owned from here on and leaving still clears it.
    stamp(s)
    persist(s)
  },
  // An opened Research Pack rides on the board as CONTEXT (Pouria, 2026-08-17: "put opened resarach
  // pack to work board and keep it until user remove it from work board"), so it survives a clear:
  // the user did not tick it, and saving or clearing a selection is not a statement about it. Its
  // own × is what takes it off.
  // …and the draft's sources survive it for the same reason: clearing a SELECTION says nothing
  // about what the reply in the composer was written from. `setEvidence` is what replaces them.
  CLEAR(s) { s.items = s.items.filter(i => i.kind === 'pack' || isEvidence(i)); stamp(s); persist(s) },
  // The sources of the draft now in the composer, replacing whatever the last draft left — a
  // re-draft of the same thread must not stack a second copy of every row.
  SET_EVIDENCE(s, items) {
    s.items = [...s.items.filter(i => !isEvidence(i)), ...(items || []).map(withObjectRef)]
    stamp(s)
    persist(s)
  },
  // "The rows now on the board are expected in <ctx>" — see ENTER_CONTEXT and `enterContextEffect`.
  HAND_OFF(s, ctx) { s.handOff = ctx || null },
  // A file taken off the board (busy-my-web#400) — board only, never a delete.
  DETACH_DOC(s, id) { if (id != null && !s.detachedDocs.includes(id)) s.detachedDocs = [...s.detachedDocs, id] },
  // …and everything back, which is what opening a different entity does.
  RESTORE_DOCS(s) { s.detachedDocs = [] },
  SET_PREVIEW(s, item) { s.preview = item },
  SET_PREVIEW_SOURCE(s, v) { s.previewSource = v },
}

export const actions = {
  add({ commit }, item) { if (item && item.id != null) commit('ADD', item) },
  remove({ commit }, id) { commit('REMOVE', id) },
  toggle({ state: s, commit }, item) {
    if (!item || item.id == null) return
    if (s.items.some(i => i.id === item.id)) commit('REMOVE', item.id)
    else commit('ADD', item)
  },
  clear({ commit }) { commit('CLEAR') },
  // Take one of the OPEN ENTITY'S files off the board. It stays attached to the entity — this is a
  // board removal and the control says so (lib/chipRemove.js's `fileRemoveLabel`).
  detachDoc({ commit }, id) { commit('DETACH_DOC', id) },
  // Whatever was taken off comes back — opening another entity is a new board.
  restoreDocs({ commit }) { commit('RESTORE_DOCS') },
  // The Sources panel's list, pushed by the draft that was just made (an empty list clears it).
  setEvidence({ commit }, items) { commit('SET_EVIDENCE', items || []) },
  // Called by the router on every completed navigation with the new route's context.
  enterContext({ commit }, ctx) { commit('ENTER_CONTEXT', ctx || null) },
  // A feature that is about to navigate and wants what it just put on the board to come along says
  // so here, BEFORE routing: `handOff({ to: 'compose:<timelineId>' })`. One navigation only.
  handOff({ commit }, payload) { commit('HAND_OFF', (typeof payload === 'string' ? payload : payload?.to) || null) },
  // Open / close the middle-of-page preview. `source` says whether the panel should show the
  // picture-list switcher ('gallery') or stay as it is ('timeline', the default).
  openPreview({ commit }, item) {
    const it = item?.item || item
    if (!it) return
    commit('SET_PREVIEW', it)
    commit('SET_PREVIEW_SOURCE', item?.source || 'timeline')
  },
  closePreview({ commit }) { commit('SET_PREVIEW', null); commit('SET_PREVIEW_SOURCE', null) },
}
